import React, { useState, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import {
  GitMerge,
  Calculator,
  ChevronRight,
  Home,
  Info,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  Plus,
  Minus,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// Helper functions
const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b !== 0) {
    const temp = b;
    b = a % b;
    a = temp;
  }
  return a;
};

const lcm = (a, b) => {
  if (a === 0 || b === 0) return 0;
  return Math.abs(a * b) / gcd(a, b);
};

const gcdMultiple = (numbers) => {
  return numbers.reduce((acc, num) => gcd(acc, num));
};

const lcmMultiple = (numbers) => {
  return numbers.reduce((acc, num) => lcm(acc, num));
};

const getPrimeFactors = (num) => {
  const factors = {};
  let n = Math.abs(num);
  
  while (n % 2 === 0) {
    factors[2] = (factors[2] || 0) + 1;
    n = n / 2;
  }
  
  for (let i = 3; i <= Math.sqrt(n); i += 2) {
    while (n % i === 0) {
      factors[i] = (factors[i] || 0) + 1;
      n = n / i;
    }
  }
  
  if (n > 2) factors[n] = 1;
  
  return factors;
};

const euclideanSteps = (a, b) => {
  const steps = [];
  a = Math.abs(a);
  b = Math.abs(b);
  
  while (b !== 0) {
    const quotient = Math.floor(a / b);
    const remainder = a % b;
    steps.push({ a, b, quotient, remainder });
    a = b;
    b = remainder;
  }
  
  return { steps, gcd: a };
};

// Visual GCD/LCM Representation
const VisualRepresentation = ({ numbers, result, type }) => {
  const maxNum = Math.max(...numbers, result);
  const scale = 200 / maxNum;
  
  return (
    <div className="space-y-4">
      {numbers.map((num, i) => (
        <div key={i} className="space-y-1">
          <div className="text-xs text-muted-foreground">Number {i + 1}: {num}</div>
          <div className="h-8 bg-muted rounded-lg overflow-hidden">
            <div
              className="h-full bg-primary/30 transition-all duration-500"
              style={{ width: `${Math.min((num * scale / 2) * 100, 100)}%` }}
            ></div>
          </div>
        </div>
      ))}
      <div className="pt-2 border-t border-border">
        <div className="text-xs text-muted-foreground mb-1">
          {type === 'gcd' ? 'GCD (Greatest Common Divisor)' : 'LCM (Least Common Multiple)'}: {result}
        </div>
        <div className="h-10 bg-muted rounded-lg overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${
              type === 'gcd' ? 'bg-chart-1' : 'bg-chart-2'
            }`}
            style={{ width: `${Math.min((result * scale / 2) * 100, 100)}%` }}
          ></div>
        </div>
      </div>
    </div>
  );
};

// Prime Factorization Visualization
const PrimeFactorViz = ({ numbers }) => {
  const factorizations = numbers.map(n => getPrimeFactors(n));
  
  // Get all unique prime factors
  const allPrimes = new Set();
  factorizations.forEach(f => {
    Object.keys(f).forEach(prime => allPrimes.add(parseInt(prime)));
  });
  const sortedPrimes = Array.from(allPrimes).sort((a, b) => a - b);
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left p-2 text-muted-foreground font-medium">Number</th>
            {sortedPrimes.map(prime => (
              <th key={prime} className="text-center p-2 text-muted-foreground font-medium">
                {prime}
              </th>
            ))}
            <th className="text-left p-2 text-muted-foreground font-medium">Factorization</th>
          </tr>
        </thead>
        <tbody>
          {numbers.map((num, i) => {
            const factors = factorizations[i];
            const factorStr = Object.entries(factors)
              .map(([base, exp]) => exp > 1 ? `${base}^${exp}` : base)
              .join(' × ');
            
            return (
              <tr key={i} className="border-b border-border">
                <td className="p-2 font-mono font-semibold text-foreground">{num}</td>
                {sortedPrimes.map(prime => (
                  <td key={prime} className="text-center p-2">
                    <span className={`font-mono text-xs ${factors[prime] ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}>
                      {factors[prime] || 0}
                    </span>
                  </td>
                ))}
                <td className="p-2 font-mono text-xs text-muted-foreground">{factorStr}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Main Calculator Component
export default function LcmGcdCalculator() {
  const [numbers, setNumbers] = useState([12, 18]);
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  const updateNumber = (index, value) => {
    const newNumbers = [...numbers];
    newNumbers[index] = value;
    setNumbers(newNumbers);
  };

  const addNumber = () => {
    if (numbers.length < 6) {
      setNumbers([...numbers, 1]);
    }
  };

  const removeNumber = (index) => {
    if (numbers.length > 2) {
      setNumbers(numbers.filter((_, i) => i !== index));
    }
  };

  // Calculate results
  const results = useMemo(() => {
    const validNumbers = numbers.filter(n => n > 0);
    
    if (validNumbers.length === 0) {
      return {
        gcd: 0,
        lcm: 0,
        isValid: false,
        euclidean: null,
      };
    }
    
    const gcdResult = gcdMultiple(validNumbers);
    const lcmResult = lcmMultiple(validNumbers);
    const euclidean = validNumbers.length === 2 ? euclideanSteps(validNumbers[0], validNumbers[1]) : null;
    
    return {
      gcd: gcdResult,
      lcm: lcmResult,
      isValid: true,
      euclidean,
      coprime: gcdResult === 1,
    };
  }, [numbers]);

  return (
    <>
      <Helmet>
        <title>LCM & GCD Calculator - Least Common Multiple & Greatest Common Divisor | MathCalcPro</title>
        <meta name="description" content="Free LCM and GCD calculator. Find the least common multiple and greatest common divisor of multiple numbers with step-by-step solutions and visualizations." />
        <meta name="keywords" content="lcm calculator, gcd calculator, least common multiple, greatest common divisor, euclidean algorithm" />
        <link rel="canonical" href="https://mathcalcpro.com/calculators/lcm-gcd" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6">
          <Link to="/" className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-1">
            <Home className="w-3 h-3" />
            <span>Home</span>
          </Link>
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
          <span className="text-foreground font-medium">LCM & GCD Calculator</span>
        </nav>

        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <GitMerge className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">LCM & GCD Calculator</h1>
              <p className="text-muted-foreground">Find least common multiple and greatest common divisor</p>
            </div>
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left Column - Calculator Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <Card className="glass-strong rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-6">
                <Calculator className="w-5 h-5 text-primary" />
                <span className="font-semibold text-foreground">Calculator</span>
              </div>

              {/* Number Inputs */}
              <div className="space-y-4">
                <Label className="text-sm text-muted-foreground mb-2 block">Enter Numbers</Label>
                
                {numbers.map((num, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={num}
                      onChange={(e) => updateNumber(index, parseInt(e.target.value) || 0)}
                      className="neumorph-inset border-0 text-center font-mono text-lg"
                      placeholder={`Number ${index + 1}`}
                    />
                    {numbers.length > 2 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeNumber(index)}
                        className="flex-shrink-0"
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}

                {numbers.length < 6 && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={addNumber}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Number
                  </Button>
                )}

                {/* Quick Examples */}
                <div>
                  <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { nums: [12, 18], label: '12, 18' },
                      { nums: [24, 36], label: '24, 36' },
                      { nums: [15, 25, 30], label: '15, 25, 30' },
                    ].map((ex, i) => (
                      <Button
                        key={i}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => setNumbers(ex.nums)}
                      >
                        {ex.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Results */}
                <div className="pt-4 border-t border-border space-y-3">
                  <Label className="text-sm font-medium text-foreground">Results</Label>
                  
                  {/* GCD */}
                  <div className="neumorph-sm rounded-xl p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">GCD (Greatest Common Divisor)</span>
                      <CopyButton text={results.gcd.toString()} />
                    </div>
                    <span className="text-2xl font-bold font-mono text-chart-1 block">
                      {results.gcd}
                    </span>
                    {results.coprime && (
                      <Badge variant="secondary" className="mt-2 text-xs">
                        Numbers are coprime
                      </Badge>
                    )}
                  </div>

                  {/* LCM */}
                  <div className="neumorph-sm rounded-xl p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs text-muted-foreground">LCM (Least Common Multiple)</span>
                      <CopyButton text={results.lcm.toString()} />
                    </div>
                    <span className="text-2xl font-bold font-mono text-chart-2 block">
                      {results.lcm}
                    </span>
                  </div>

                  {/* LCM/GCD Relationship */}
                  {numbers.length === 2 && results.isValid && (
                    <div className="neumorph-sm rounded-xl p-3 text-xs">
                      <div className="text-muted-foreground mb-1">Verification:</div>
                      <div className="font-mono text-foreground">
                        {numbers[0]} × {numbers[1]} = {numbers[0] * numbers[1]}
                      </div>
                      <div className="font-mono text-foreground">
                        GCD × LCM = {results.gcd} × {results.lcm} = {results.gcd * results.lcm}
                      </div>
                      {numbers[0] * numbers[1] === results.gcd * results.lcm && (
                        <Badge variant="default" className="mt-2 text-xs">
                          ✓ Verified
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Center/Right Column - Visualization & Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Visualization Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="glass rounded-2xl p-6">
                <Tabs defaultValue="visual" className="w-full">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-foreground flex items-center gap-2">
                      <Info className="w-4 h-4 text-primary" />
                      Visualizations
                    </h2>
                    <TabsList className="neumorph-sm">
                      <TabsTrigger value="visual" className="text-xs">Visual</TabsTrigger>
                      <TabsTrigger value="prime" className="text-xs">Prime Factors</TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="visual" className="mt-0">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="neumorph-sm rounded-xl p-4">
                        <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                          <div className="w-3 h-3 rounded bg-chart-1"></div>
                          GCD Visualization
                        </h3>
                        <VisualRepresentation numbers={numbers} result={results.gcd} type="gcd" />
                      </div>
                      <div className="neumorph-sm rounded-xl p-4">
                        <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                          <div className="w-3 h-3 rounded bg-chart-2"></div>
                          LCM Visualization
                        </h3>
                        <VisualRepresentation numbers={numbers} result={results.lcm} type="lcm" />
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="prime" className="mt-0">
                    <div className="neumorph-sm rounded-xl p-4">
                      <h3 className="text-sm font-medium text-foreground mb-4">Prime Factorization Method</h3>
                      <PrimeFactorViz numbers={numbers} />
                      <div className="mt-4 p-3 bg-muted/30 rounded-lg text-xs text-muted-foreground">
                        <p className="mb-1"><strong>GCD:</strong> Take the minimum power of each common prime factor</p>
                        <p><strong>LCM:</strong> Take the maximum power of each prime factor</p>
                      </div>
                    </div>
                  </TabsContent>
                </Tabs>
              </Card>
            </motion.div>

            {/* Euclidean Algorithm (for 2 numbers) */}
            {results.euclidean && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Card className="glass rounded-2xl p-6">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                    <BookOpen className="w-4 h-4 text-primary" />
                    Euclidean Algorithm
                  </h2>
                  <div className="space-y-2">
                    {results.euclidean.steps.map((step, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm font-mono">
                        <span className="text-muted-foreground">{step.a}</span>
                        <span className="text-muted-foreground">=</span>
                        <span className="text-muted-foreground">{step.b}</span>
                        <span className="text-muted-foreground">×</span>
                        <span className="text-foreground font-semibold">{step.quotient}</span>
                        <span className="text-muted-foreground">+</span>
                        <span className="text-primary font-semibold">{step.remainder}</span>
                      </div>
                    ))}
                    <div className="mt-4 p-3 bg-primary/10 rounded-lg">
                      <span className="text-sm">
                        GCD = <span className="font-mono font-bold text-primary">{results.euclidean.gcd}</span>
                      </span>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Step-by-Step Explanation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="glass rounded-2xl p-6">
                <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                  <BookOpen className="w-4 h-4 text-primary" />
                  Step-by-Step Solution
                </h2>

                <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                  <AccordionItem value="step-1" className="border-b border-border">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">1</span>
                        <span className="font-medium">Identify the numbers</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-2">
                        <p className="text-muted-foreground">
                          Your numbers: <span className="font-mono text-foreground font-semibold">{numbers.join(', ')}</span>
                        </p>
                        <p className="text-muted-foreground">
                          We will find both the GCD and LCM of these numbers.
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="step-2" className="border-b border-border">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">2</span>
                        <span className="font-medium">Calculate GCD</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-3">
                        <p className="text-muted-foreground">
                          The GCD (Greatest Common Divisor) is the largest number that divides all given numbers evenly.
                        </p>
                        {numbers.length === 2 && results.euclidean && (
                          <>
                            <p className="text-muted-foreground">Using the Euclidean algorithm:</p>
                            <div className="formula-display">
                              GCD({numbers[0]}, {numbers[1]}) = <span className="text-primary font-semibold">{results.gcd}</span>
                            </div>
                          </>
                        )}
                        {numbers.length > 2 && (
                          <div className="formula-display">
                            GCD({numbers.join(', ')}) = <span className="text-primary font-semibold">{results.gcd}</span>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="step-3" className="border-0">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="step-badge">3</span>
                        <span className="font-medium">Calculate LCM</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pl-11 space-y-3">
                        <p className="text-muted-foreground">
                          The LCM (Least Common Multiple) is the smallest number that is a multiple of all given numbers.
                        </p>
                        {numbers.length === 2 && (
                          <>
                            <p className="text-muted-foreground">Using the formula: LCM(a,b) = (a × b) / GCD(a,b)</p>
                            <div className="formula-display">
                              LCM = ({numbers[0]} × {numbers[1]}) / {results.gcd} = {numbers[0] * numbers[1]} / {results.gcd} = <span className="text-primary font-semibold">{results.lcm}</span>
                            </div>
                          </>
                        )}
                        {numbers.length > 2 && (
                          <div className="formula-display">
                            LCM({numbers.join(', ')}) = <span className="text-primary font-semibold">{results.lcm}</span>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            </motion.div>

            {/* Deep Dive Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-6"
            >
              {/* What are LCM and GCD */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">What are LCM and GCD?</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Greatest Common Divisor (GCD)</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      The GCD, also called the Greatest Common Factor (GCF) or Highest Common Factor (HCF), is the largest positive 
                      integer that divides each of the given numbers without leaving a remainder. For example, the GCD of 12 and 18 
                      is 6 because 6 is the largest number that divides both 12 and 18 evenly.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Least Common Multiple (LCM)</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      The LCM is the smallest positive integer that is divisible by each of the given numbers. For example, the LCM 
                      of 12 and 18 is 36 because 36 is the smallest number that both 12 and 18 divide into evenly. LCM is useful 
                      when adding fractions with different denominators.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Methods & Properties */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-primary" />
                  Methods & Key Properties
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { name: 'Euclidean Algorithm', desc: 'Efficient method for finding GCD using division' },
                    { name: 'Prime Factorization', desc: 'Find GCD/LCM using prime factors' },
                    { name: 'GCD × LCM = a × b', desc: 'For two numbers a and b' },
                    { name: 'Coprime Numbers', desc: 'When GCD = 1, numbers are coprime' },
                  ].map((item, i) => (
                    <div key={i} className="bg-muted/30 rounded-xl p-4">
                      <span className="text-sm font-semibold text-foreground block mb-1">{item.name}</span>
                      <span className="text-xs text-muted-foreground">{item.desc}</span>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Real World Applications */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
                <ul className="space-y-3">
                  {[
                    'LCM: Finding when two periodic events will coincide (e.g., bus schedules, planetary alignments)',
                    'GCD: Simplifying fractions to their lowest terms',
                    'LCM: Adding or subtracting fractions by finding common denominators',
                    'GCD: Distributing items into equal groups without leftovers',
                    'LCM: Tiling floors with different sized tiles',
                    'GCD: Cutting materials into equal-sized pieces with minimal waste',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ArrowRight className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* Common Mistakes */}
              <Card className="rounded-2xl p-6 bg-destructive/5 border border-destructive/20">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                  Common Mistakes to Avoid
                </h2>
                <ul className="space-y-3">
                  {[
                    'Confusing GCD with LCM (GCD is always ≤ smallest number, LCM is always ≥ largest number)',
                    'Forgetting that GCD(a, 0) = a and LCM(a, 0) = 0',
                    'Thinking LCM is always the product of the numbers (only true when GCD = 1)',
                    'Not simplifying when finding GCD/LCM of fractions',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* FAQ Section */}
              <Card className="rounded-2xl p-6 bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    {
                      q: 'What is the relationship between GCD and LCM?',
                      a: 'For any two positive integers a and b, the product GCD(a,b) × LCM(a,b) = a × b. This relationship is useful for finding one value when you know the other. However, this only works for exactly two numbers, not three or more.'
                    },
                    {
                      q: 'What does it mean when GCD equals 1?',
                      a: 'When GCD(a, b) = 1, the numbers are called "coprime" or "relatively prime." This means they share no common factors other than 1. For example, 15 and 28 are coprime even though neither is a prime number.'
                    },
                    {
                      q: 'How is the Euclidean algorithm more efficient?',
                      a: 'The Euclidean algorithm finds GCD by repeatedly dividing and taking remainders, which is much faster than listing all factors, especially for large numbers. It has a time complexity of O(log min(a,b)), making it very efficient.'
                    },
                    {
                      q: 'Can you find GCD and LCM of more than two numbers?',
                      a: 'Yes! For multiple numbers, apply the operations pairwise. For example, GCD(a,b,c) = GCD(GCD(a,b), c). The same approach works for LCM. The prime factorization method is often clearer for three or more numbers.'
                    },
                    {
                      q: 'Why is LCM useful for adding fractions?',
                      a: 'To add fractions with different denominators, you need a common denominator. The LCM of the denominators gives you the least common denominator (LCD), which is the smallest number both denominators divide into, minimizing the need for simplification.'
                    }
                  ].map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="font-medium text-foreground">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}
